import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/account/offertes/")({
  head: () => ({ meta: [{ title: "Mijn offertes" }, { name: "robots", content: "noindex" }] }),
  component: QuotesPage,
});

function QuotesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-quotes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("id, quote_number, title, status, total_cents, created_at, valid_until")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const copyDeepLink = async (id: string) => {
    const url = `${window.location.origin}/account/offertes/${encodeURIComponent(id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link naar offerte gekopieerd");
    } catch {
      toast.error("Kopiëren mislukt — kopieer handmatig", { description: url });
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Mijn offertes</h1>
      {isLoading && <p>Laden…</p>}
      {data && data.length === 0 && (
        <p className="text-muted-foreground">Je hebt nog geen offertes. Neem contact op via de website om er een aan te vragen.</p>
      )}
      <div className="space-y-2">
        {data?.map((q) => (
          <div
            key={q.id}
            className="group relative flex items-center gap-3 rounded-lg border bg-card p-4 hover:border-primary"
          >
            <Link
              to="/account/offertes/$id"
              params={{ id: q.id }}
              className="flex flex-1 items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold">{q.title}</div>
                <div className="text-xs text-muted-foreground">
                  #{q.quote_number} · {new Date(q.created_at).toLocaleDateString("nl-NL")}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold">€ {(q.total_cents / 100).toFixed(2)}</div>
                <StatusBadge status={q.status} />
              </div>
            </Link>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copyDeepLink(q.id);
              }}
              title="Kopieer directe link naar deze offerte"
              aria-label="Kopieer directe link naar deze offerte"
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition hover:border-border hover:bg-muted hover:text-foreground"
            >
              <Link2 className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    sent: "bg-blue-100 text-blue-800",
    signed: "bg-green-100 text-green-800",
    declined: "bg-[#3a7bd5] text-[#3a7bd5]",
    draft: "bg-gray-100 text-gray-800",
  };
  return <span className={`mt-1 inline-block rounded px-2 py-0.5 text-xs ${map[status] ?? "bg-gray-100"}`}>{status}</span>;
}
